<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import type { MerchantEquipListRes, VendorEquipListType } from '@/types/black-market'
import type { EquipItemType } from '@/types/equip'
import { getEquipName } from '@/utils/equip'
import { socketEmitAsync } from '@/utils/web-socket'
import { computed, onMounted, ref } from 'vue'
import EquipInfo from '@/views/item/components/EquipInfo.vue'
import { formatNumberWithCommas, formatNumberWithWan } from '@/utils'
import { useUserStore } from '@/stores/modules/user'
import type { ItemType } from '@/types/item'
import { QuestionFilled } from '@element-plus/icons-vue'
import type { warehouseApiRes } from '@/types/warehouse'

const userStore = useUserStore()

const merchantInfoList = ref<VendorEquipListType[]>([])
const equipInfo = ref<EquipItemType & { price: number; merchantId: number }>()
const buyRecord = ref<number[]>([])

const merchantItemList = computed(() => {
  return merchantInfoList.value.map((item) => {
    const info = equipCollection[item.equipId]
    return {
      ...info,
      ...item,
      reinforcedLevel: 0,
      isLock: false,
      price: item.price,
      merchantId: Number(item.id),
    }
  })
})

async function getMerchantEquipList() {
  const res = await socketEmitAsync<MerchantEquipListRes>({ type: 'getMerchantInfoList' })
  merchantInfoList.value = res.merchantInfoList
  buyRecord.value = res.buyRecord
}
async function sellMerchantEquip() {
  if (!equipInfo.value?.name) return ElMessage.warning('请先选择收购品')
  const hasEquipList = userStore.itemList.filter((item) => {
    if (item.isLock || item.reinforcedLevel || item.starAttrs?.length) return false
    return item.equipId === equipInfo.value?.equipId
  })
  if (hasEquipList.length === 0) {
    const warehouseHasEquipList = userStore.warehouseItemList?.filter((item) => {
      if (item.isLock || item.reinforcedLevel || item.starAttrs?.length) return false
      return item.equipId === equipInfo.value?.equipId
    })
    if (warehouseHasEquipList?.length) {
      const id = warehouseHasEquipList[0].id
      const action = await ElMessageBox.confirm(
        `<p class="affix">在仓库中寻到<span class="info">未强化、未锁定、未升星</span>的【<span class="${equipInfo.value.quality}">${equipInfo.value.name}</span>】，是否取出？</p>`,
        '提示',
        {
          type: 'warning',
          dangerouslyUseHTMLString: true,
          appendTo: 'body',
          confirmButtonClass: 'is-plain',
        },
      )
      if (action !== 'confirm') return
      await socketEmitAsync<warehouseApiRes>({ type: 'warehouseTakeOut', data: { id } })
      if (!userStore.warehouseItemList) return ElMessage.warning('客户端错误，请尝试重启游戏')
      const itemIndex = userStore.warehouseItemList.findIndex((item) => item.id === id)
      if (itemIndex === -1) return
      userStore.itemList.unshift(userStore.warehouseItemList[itemIndex])
      userStore.warehouseItemList.splice(itemIndex, 1)
      ElMessage.success('取出成功')
      return
    }
    return ElMessage.warning('未找到符合条件的装备')
  }
  const itemId = hasEquipList[0].id
  ElMessageBox.confirm(
    `<p class="affix">已为您自动找寻到<span class="info">未强化、未锁定、未升星</span>的【<span class="${equipInfo.value.quality}">${equipInfo.value.name}</span>】，请确认是否出售？（该操作将导致您背包中<span class="shrink">失去</span>一件对应装备）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      if (!equipInfo.value) return
      await socketEmitAsync<ItemType>({ type: 'sellToMerchantEquip', data: { id: itemId } })
      const { price, merchantId } = equipInfo.value
      userStore.gold += price
      buyRecord.value.push(merchantId)
      const index = userStore.itemList.findIndex((item) => item.id === itemId)
      userStore.itemList.splice(index, 1)
      ElMessage.success('售卖成功')
    })
    .catch(() => {})
}

onMounted(() => {
  getMerchantEquipList()
})
</script>

<template>
  <div class="merchant-wrap">
    <p class="property">
      <span>💰 {{ formatNumberWithWan(userStore.gold) }}</span>
      <span style="margin-left: 12px">💎 {{ formatNumberWithWan(userStore.diamond) }}</span>
    </p>
    <div class="merchant">
      <div class="left">
        <div class="border-wrap" style="flex: 1; display: flex; flex-flow: column nowrap; overflow: hidden">
          <h4>收购列表</h4>
          <div class="merchant-equip-list-wrap">
            <div class="merchant-equip-list">
              <div class="item affix" v-for="item of merchantItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  type="primary"
                  plain
                  @click="equipInfo = item"
                  :style="{ borderColor: item.id === equipInfo?.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="right border-wrap">
        <h4>装备信息</h4>
        <template v-if="equipInfo">
          <EquipInfo :equip-info="equipInfo" :show-action="false" />
        </template>
      </div>
    </div>
    <div class="action border-wrap">
      <div class="help-info">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>行商说明</h4>
            <p>
              1. 行商每日造访，带来一批需要收购的商品，您可以选择背包中的装备<span class="info">售卖</span>给行商以获得<span class="gold">金币</span
              >。
            </p>
            <p>
              2. 收购价格随行商每次到来浮动，<span class="unique">独特</span>收购价在<span class="gold">10万 ~ 30万</span>，<span class="legend"
                >传说</span
              >装备收购价在<span class="gold">20万 ~ 50万</span>。
            </p>
            <p>3. 每种商品行商仅需要一件，您无法重复售卖，同时已强化已升星已锁定的装备无法被售卖。</p>
            <p>4. <b style="color: red">装备属性会随版本更迭持续变化，售出时请慎重。</b></p>
          </div>
        </el-popover>
      </div>
      <h4>交易</h4>
      <p>装备名称：<span v-html="getEquipName(equipInfo, equipInfo?.type)"></span></p>
      <p class="affix">
        收购价：
        <template v-if="equipInfo">
          <span class="gold">{{ formatNumberWithCommas(equipInfo.price) }}</span>
        </template>
        <span v-else>（未选择）</span>
      </p>
      <el-button
        type="success"
        plain
        :disabled="buyRecord.includes(equipInfo?.merchantId || 0)"
        @click="sellMerchantEquip"
        style="margin-bottom: 10px"
      >
        {{ buyRecord.includes(equipInfo?.merchantId || 0) ? '已售卖' : '售卖' }}
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.merchant-wrap {
  .border-wrap {
    padding: 10px;
  }
  .property {
    font-size: 14px;
    margin: 0 0 10px 0;
    text-align: right;
  }
  .merchant {
    flex: 1;
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    overflow: hidden;
    .left {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
    }
    .right {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
    }

    .merchant-equip-list-wrap {
      margin-bottom: 10px;
      flex: 1;
      overflow: auto;
      .merchant-equip-list {
        padding: 0 5px;
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;
        overflow: auto;
        .item {
          width: 100%;
          margin-bottom: 10px;
          button {
            width: 100%;
          }
        }
      }
    }
    :deep() {
      .el-button {
        width: 100%;
      }
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
  .action {
    flex-shrink: 0;
    position: relative;
    button {
      width: 100%;
    }
    p {
      margin: 8px 0;
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    margin-top: 10px;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .item-btn-wrap {
      overflow: auto;
    }
  }
}
</style>
